import { styled, useTheme } from '@mui/material/styles';
import {
  Drawer,
  List,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Box,
  Typography,
  Popover,
  Chip,
  Button,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import PhonelinkRingIcon from '@mui/icons-material/PhonelinkRing';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES, getVoiceAgentPath, getDemoCallPath } from '../../constants/routes';
import { useState, useEffect } from 'react';
import { businessConfigService } from '../../services/businessConfigService';
import { initFirebase } from '../../firebase';

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const SideNav = ({ open, toggleDrawer, drawerWidth, isMobile }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isBusinessOwner, customerIds, user } = useAuth();
  
  // State for user profile popover
  const [anchorEl, setAnchorEl] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasDemoConfig, setHasDemoConfig] = useState(false);

  // Fetch business name if user is business owner
  useEffect(() => {
    const fetchBusinessName = async () => {
      if (isBusinessOwner && customerIds && customerIds.length > 0) {
        setLoading(true);
        try {
          const customerId = customerIds[0];
          const businessData = await businessConfigService.getBusinessConfig(customerId);
          setBusinessName(businessData.business_name || customerId);
          setHasDemoConfig(!!businessData.isp_demo_config);
        } catch (error) {
          console.error('Error fetching business name:', error);
          setBusinessName(customerIds[0]);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchBusinessName();
  }, [isBusinessOwner, customerIds]);

  const handleUserClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      const { auth } = initFirebase();
      await auth.signOut();
      navigate(ROUTES.LOGIN);
      handleUserClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const openUserPopover = Boolean(anchorEl);

  // Customer menu items (top)
  const customerMenuItems = [
    { text: t('nav.home'), icon: <HomeIcon />, path: ROUTES.HOME },
    { text: t('nav.myBookings'), icon: <EventIcon />, path: ROUTES.MY_BOOKINGS },
  ];

  // Business owner menu items (bottom, above user info)
  const businessOwnerMenuItems = [];
  if (isBusinessOwner && customerIds && customerIds.length > 0) {
    const customerId = customerIds[0]; // Use first customer ID
    
    businessOwnerMenuItems.push({
      text: t('nav.businessSettings'),
      icon: <SettingsIcon />,
      path: `/settings/business/${customerId}`,
    });
    businessOwnerMenuItems.push({
      text: t('nav.calendarConfig'),
      icon: <CalendarMonthIcon />,
      path: `/settings/calendar/${customerId}`,
    });
    businessOwnerMenuItems.push({
      text: t('nav.calendar'),
      icon: <EventIcon />,
      path: `/calendar/${customerId}`,
    });
    
    // Add Voice Agent menu item only for fluente-demo business
    if (customerId === 'fluente-demo') {
      businessOwnerMenuItems.push({
        text: t('nav.voiceAgent'),
        icon: <PhoneIcon />,
        path: getVoiceAgentPath(customerId),
      });
    }
    
    // Add DEMO menu item if demo config exists
    if (hasDemoConfig) {
      businessOwnerMenuItems.push({
        text: 'DEMO',
        icon: <PhonelinkRingIcon />,
        path: getDemoCallPath(customerId),
      });
    }
  }

  const handleNavigation = (path, state) => {
    if (state) {
      navigate(path, { state });
    } else {
      navigate(path);
    }
    if (isMobile) {
      toggleDrawer();
    }
  };

  const drawer = (
    <>
      <DrawerHeader>
        <IconButton onClick={toggleDrawer}>
          {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </DrawerHeader>
      <Divider />
      
      {/* Customer menu items */}
      <List>
        {customerMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => handleNavigation(item.path, item.state)}
              selected={location.pathname === item.path}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                sx={{ opacity: open ? 1 : 0 }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Spacer to push business owner items and user info to bottom */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Business owner menu items */}
      {businessOwnerMenuItems.length > 0 && (
        <>
          <Divider />
          <List>
            {businessOwnerMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path, item.state)}
                  selected={location.pathname === item.path}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    sx={{ opacity: open ? 1 : 0 }} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* User info panel at bottom */}
      {user && (
        <>
          <Divider />
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={handleUserClick}
              sx={{
                minHeight: 64,
                justifyContent: open ? 'initial' : 'center',
                px: 2,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 40,
                    height: 40,
                  }}
                >
                  {user.email?.charAt(0).toUpperCase() || <PersonIcon />}
                </Avatar>
              </ListItemIcon>
              {open && (
                <Box sx={{ 
                  overflow: 'hidden',
                  flexGrow: 1,
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {user.displayName || user.email?.split('@')[0] || 'User'}
                  </Typography>
                  {isBusinessOwner && businessName && (
                    <>
                      <Chip
                        label={t('role.businessOwner') || 'Business Owner'}
                        size="small"
                        color="primary"
                        sx={{ 
                          height: 18,
                          fontSize: '0.65rem',
                          mt: 0.5,
                          mb: 0.25,
                        }}
                      />
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 12 }} />
                        {businessName}
                      </Typography>
                    </>
                  )}
                  {!isBusinessOwner && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                    >
                      {user.email}
                    </Typography>
                  )}
                </Box>
              )}
            </ListItemButton>
          </ListItem>

          {/* User Profile Popover */}
          <Popover
            open={openUserPopover}
            anchorEl={anchorEl}
            onClose={handleUserClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            sx={{
              '& .MuiPopover-paper': {
                minWidth: 280,
                p: 2,
              },
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 64,
                  height: 64,
                  margin: '0 auto',
                  mb: 1,
                }}
              >
                {user.email?.charAt(0).toUpperCase() || <PersonIcon />}
              </Avatar>
              <Typography variant="h6" gutterBottom>
                {user.displayName || user.email?.split('@')[0] || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {user.email}
              </Typography>
              {isBusinessOwner && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={t('role.businessOwner') || 'Business Owner'}
                    color="primary"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  {businessName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {businessName}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                color="error"
              >
                {t('auth.signOut') || 'Sign Out'}
              </Button>
            </Box>
          </Popover>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? drawerWidth : 65,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : 65,
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default SideNav;
